import { message, Radio } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getAnalytics, getStatistics } from 'requests/statistics';
import iconTeacher from 'assets/images/Icon-sidebar-1.svg';
import iconStudent from 'assets/images/Icon-sidebar-2.svg';
import iconClass from 'assets/images/Icon-sidebar-3.svg';
import iconMaterial from 'assets/images/Icon-sidebar-4.svg';
import dayjs from 'utils/day';
import { DualAxes } from '@ant-design/plots';

export default function Dashboard() {
    const { t } = useTranslation();
    const location = useLocation();
    const [statistics, setStatistics] = useState();
    const [analytics, setAnalytics] = useState([]);
    const [analyticsType, setAnalyticsType] = useState('week');
    const [userMax, setUserMax] = useState(0);
    const [practiceMax, setPracticeMax] = useState(0);

    useEffect(() => {
        getStatistics()
            .then((data) => {
                setStatistics(data);
            })
            .catch((e) => {
                message.error(t(e.response?.data.message || 'error'));
            });
    }, [location]);

    useEffect(() => {
        const params = {
            startDate: dayjs().startOf(analyticsType).format('YYYYMMDD'),
            endDate: dayjs().endOf(analyticsType).format('YYYYMMDD'),
        };

        getAnalytics(params)
            .then((data) => {
                const max = {
                    user: 0,
                    practice: 0,
                };

                for (const each of data) {
                    if (analyticsType === 'week') {
                        each.date = dayjs(each.date, 'YYYY-MM-DD').format('ddd');
                    } else {
                        each.date = dayjs(each.date, 'YYYY-MM-DD').format('DD');
                    }

                    max.user = Math.max(each.user, max.user);
                    max.practice = Math.max(each.N4, each.N5, max.practice);
                }

                max.user = Math.pow(10, Math.ceil(Math.log10(Math.ceil(max.user / 4)))) * 4;
                max.practice = Math.pow(10, Math.ceil(Math.log10(Math.ceil(max.practice / 4)))) * 4;

                setUserMax(max.user);
                setPracticeMax(max.practice);
                setAnalytics(data);
            })
            .catch((e) => {
                message.error(t(e.response?.data.message || 'error'));
            });
    }, [analyticsType]);

    const handleTypeChange = (e) => {
        setAnalyticsType(e.target.value);
    };

    const statisticData = statistics
        ? [
              {
                  icon: iconTeacher,
                  data: [{ title: t('teacher_dashboard'), number: statistics.teacher }],
              },
              {
                  icon: iconStudent,
                  data: [{ title: t('student_dashboard'), number: statistics.student }],
              },
              {
                  icon: iconClass,
                  data: [{ title: t('class_dashboard'), number: statistics.class }],
              },
              {
                  icon: iconMaterial,
                  data: [
                      { title: 'N5', number: statistics.kanji.N5 },
                      { title: 'N4', number: statistics.kanji.N4 },
                  ],
              },
          ]
        : [];

    const config = {
        data: [
            analytics.map((e) => ({ date: e.date, user: Number(e.user) })),
            [
                ...analytics.map((e) => ({ date: e.date, count: Number(e.N5), type: t('analytics_n5') })),
                ...analytics.map((e) => ({ date: e.date, count: Number(e.N4), type: t('analytics_n4') })),
            ],
        ],
        xField: 'date',
        yField: ['user', 'count'],
        yAxis: {
            user: {
                min: 0,
                tickCount: 5,
                // max: 40,
                max: userMax,
            },
            count: {
                min: 0,
                tickCount: 5,
                // max: 100,
                max: practiceMax,
            },
        },
        geometryOptions: [
            {
                geometry: 'column',
                color: '#CA3D45',
                maxColumnWidth: 40,
            },
            {
                geometry: 'line',
                seriesField: 'type',
                color: ['#58BD7D', '#F7E11A'],
            },
        ],
        meta: {
            user: {
                alias: t('analytics_user'),
            },
        },
        legend: {
            layout: 'vertical',
            position: 'bottom-left',
        },
        interactions: [{ type: 'element-highlight' }, { type: 'legend-highlight' }, { type: 'active-region' }],
    };

    return (
        <>
            <div className="dashboard">
                <h1 className="title">{t('overview')}</h1>
                <div className="statistics mt-12">
                    {statisticData.map((e, i) => (
                        <div className="statistic-card" key={i}>
                            <img src={e.icon} alt="" />
                            <div className="data mt-8">
                                {e.data.map((each, index) => (
                                    <div className="item" key={index}>
                                        <div className="title">{each.title}</div>
                                        <div className="number mt-8">{each.number}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="analytics mt-24">
                    <div className="header">
                        <h1 className="title">{t('analytics')}</h1>
                        <Radio.Group
                            onChange={handleTypeChange}
                            value={analyticsType}
                            buttonStyle="solid"
                            optionType="button"
                        >
                            <Radio.Button value="week">{t('weekly')}</Radio.Button>
                            <Radio.Button value="month">{t('monthly')}</Radio.Button>
                        </Radio.Group>
                    </div>
                    <div className={`chart-container ${analyticsType} mt-24`}>
                        <DualAxes {...config} height={360} />
                    </div>
                </div>
            </div>
        </>
    );
}
